import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { WikiItemType } from '../services/generated/Api';
import type { Language } from '../i18n/types';
import { wikiContentLang } from '../i18n/wikiLanguages';
import type { WikiItemType as WikiItemTypeName } from '../screens/wiki/wikiItem.types';

export type WikiDropsTarget = { entity: 'skin' | 'weapon'; slug: string } | { entity: 'item'; type: WikiItemTypeName; slug: string };

const ITEM_TYPE: Record<WikiItemTypeName, WikiItemType> = {
  sticker: WikiItemType.Sticker,
  agent: WikiItemType.Agent,
  charm: WikiItemType.Charm,
  music_kit: WikiItemType.MusicKit,
  patch: WikiItemType.Patch,
  graffiti: WikiItemType.Graffiti,
  highlight: WikiItemType.Highlight,
  pin: WikiItemType.Pin,
  pass: WikiItemType.Pass,
  collectible: WikiItemType.Collectible,
  key: WikiItemType.Key,
};

export function useWikiDrops(language: Language, target: WikiDropsTarget) {
  const lang = wikiContentLang(language);
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.wikiDrops(lang, target.entity === 'item' ? target.type : target.entity, target.slug),
    queryFn: async () => {
      if (target.entity === 'item') {
        return (await apiClient.api.wikiControllerGetWikiItemDrops(lang, ITEM_TYPE[target.type], target.slug)).drops;
      }
      const response =
        target.entity === 'skin'
          ? await apiClient.api.wikiControllerGetWikiSkinDrops(lang, target.slug)
          : await apiClient.api.wikiControllerGetWikiWeaponDrops(lang, target.slug);
      return response.drops;
    },
    staleTime: 60 * 1000,
    // WHY: при ошибке блок просто не показывается — повторы держали бы скелет на экране ещё несколько секунд
    retry: false,
  });
}
